import { PayloadAction } from '@reduxjs/toolkit';
import {
  ConfigSelectors,
  LanguageService,
  NotificationActions,
  StatsActions,
  StatsSelectors,
  getWorkoutId,
} from '@web_opxp/features';
import { all, call, delay, put, select, take, takeLatest } from 'redux-saga/effects';
import {
  CYCLE_INTENSITY,
  ColourblindnessCategoryGroupIds,
  DEFAULT_CATEGORY_ID,
  DOMAIN_STAT,
  DyscalculiaCategoryGroupIds,
  LED_CONSENT_STAT,
  WORKOUT_PREFERENCE_STAT,
} from '../base/constants';
import { EExerciseCategory } from '../base/types';
import { makeExerciseCategoryStatString } from '../base/utils';
import { CyclesActions } from '../cycles';
import { ColourblindnessStatId, DyscalculiaStatId } from './constants';
import { PersonalizationActions } from './slice';
import {
  EPersonalizationErrors,
  ISavePersonalizationDomainStatsPayload,
  ISavePersonalizationLEDStatPayload,
  ISavePersonalizationWorkoutStatsPayload,
} from './types';

export function* saveDomainStatSaga(action: PayloadAction<ISavePersonalizationDomainStatsPayload>) {
  const { domain, stat, userId } = action.payload;
  yield put(
    StatsActions.updateGenericStat(
      DOMAIN_STAT,
      {
        statComponentId: domain,
        valueInt: stat,
        valueTxt: '',
      },
      userId
    )
  );
  yield delay(3000);
  yield call(saveSettings, true, userId);
}

export function* saveWorkoutStatSaga(action: PayloadAction<ISavePersonalizationWorkoutStatsPayload>) {
  const { workoutId, workoutEnabled, disableImpairment, userId } = action.payload;
  if (!disableImpairment) {
    // if we don't need to handle impairment - do the normal flow
    yield put(
      StatsActions.updateGenericStat(
        WORKOUT_PREFERENCE_STAT,
        {
          statComponentId: workoutId,
          valueInt: workoutEnabled ? 0 : 1,
          valueTxt: '',
        },
        userId
      )
    );
    yield delay(3000);
    yield call(saveSettings, true, userId);
    return;
  }
  // in case of disabling impairment - define which one and dispatch corresponding action
  const categoryGroupId = yield select(ConfigSelectors.getCategoryGroupIdForWorkout, workoutId);
  if (ColourblindnessCategoryGroupIds.includes(categoryGroupId)) {
    yield put(PersonalizationActions.saveColourblindnessStat(false, userId));
  }
  if (DyscalculiaCategoryGroupIds.includes(categoryGroupId)) {
    yield put(PersonalizationActions.saveDyscalculiaStat(false, userId));
  }
}

export function* saveLEDStatSaga(action: PayloadAction<ISavePersonalizationLEDStatPayload>) {
  const { stat, userId } = action.payload;
  yield put(StatsActions.updateBooleanStat(LED_CONSENT_STAT, stat, userId));
  yield delay(3000);
  yield call(saveSettings, false, userId);
}

export function* saveTrainingIngensitySaga(action: PayloadAction<number>) {
  const trainingIntensity = action.payload;
  yield put(StatsActions.updateNumericStat(CYCLE_INTENSITY, trainingIntensity));
  yield delay(3000);
  yield call(saveSettings);
}

export function* getWorkoutIdFromCategoryGroupId(categoryGroupId: number) {
  const categoryIdStat: EExerciseCategory | undefined = yield select(
    StatsSelectors.getStatIntValueById,
    makeExerciseCategoryStatString(categoryGroupId)
  );
  // if stat is not set use default category (normal difficulty)
  const categoryId = categoryIdStat === undefined ? DEFAULT_CATEGORY_ID : categoryIdStat;
  return getWorkoutId(categoryId, categoryGroupId);
}

export function* saveColourblindnessStatSaga(action: PayloadAction<ISavePersonalizationLEDStatPayload>) {
  const { stat, userId } = action.payload;
  const enableWorkout = !stat;
  // disable/enable workouts
  for (const categoryGroupId of ColourblindnessCategoryGroupIds) {
    const workoutId = yield call(getWorkoutIdFromCategoryGroupId, categoryGroupId);
    yield put(
      StatsActions.updateGenericStat(
        WORKOUT_PREFERENCE_STAT,
        {
          statComponentId: workoutId,
          valueInt: enableWorkout ? 0 : 1,
          valueTxt: '',
        },
        userId
      )
    );
  }
  // save disability stat
  yield put(StatsActions.updateGenericStat(ColourblindnessStatId, { valueInt: stat ? 1 : 0 }, userId));
  yield delay(3000);
  yield call(saveSettings, true, userId);
}

export function* saveDyscalculiaStatSaga(action: PayloadAction<ISavePersonalizationLEDStatPayload>) {
  const { stat, userId } = action.payload;
  const enableWorkout = !stat;
  // disable/enable workouts
  for (const categoryGroupId of DyscalculiaCategoryGroupIds) {
    const workoutId = yield call(getWorkoutIdFromCategoryGroupId, categoryGroupId);
    yield put(
      StatsActions.updateGenericStat(
        WORKOUT_PREFERENCE_STAT,
        {
          statComponentId: workoutId,
          valueInt: enableWorkout ? 0 : 1,
          valueTxt: '',
        },
        userId
      )
    );
  }
  // save disability stat
  yield put(StatsActions.updateGenericStat(DyscalculiaStatId, { valueInt: stat ? 1 : 0 }, userId));
  yield delay(3000);
  yield call(saveSettings, true, userId);
}

export function* saveSettings(shouldRecreate = false, userId?: number) {
  const t = LanguageService.getTFunction();
  yield put(StatsActions.saveStatsQueue(userId));
  const result = yield take([StatsActions.saveStatsQueueSuccess, StatsActions.saveStatsQueueFailed]);
  if (result.type === StatsActions.saveStatsQueueSuccess.type) {
    if (shouldRecreate && !userId) {
      // start recreating training
      yield put(CyclesActions.createTraining());
      const result = yield take([CyclesActions.trainingCreated, CyclesActions.createTrainingFailed]);
      if (result.type === CyclesActions.createTrainingFailed.type) {
        yield put(NotificationActions.showError(t('Personalization.SaveError')));
        return;
      }
      // reload cycles information after creating the new cycle
      yield put(CyclesActions.loadCycles());
      yield take(CyclesActions.cyclesReady);
    }
    if (!userId) {
      yield put(NotificationActions.showSuccess(t('Personalization.SaveSuccess')));
    } else {
      yield put(NotificationActions.showSuccess(t('Personalization.UserSaveSuccess')));
    }
    return;
  }
  switch (result.payload) {
    case EPersonalizationErrors.domainError:
      yield put(NotificationActions.showError(t('Personalization.DomainError')));
      break;
    case EPersonalizationErrors.workoutsError:
      yield put(NotificationActions.showError(t('Personalization.WorkoutsSelectError')));
      break;
    default:
      yield put(NotificationActions.showError(t('Personalization.SaveError')));
      break;
  }
}

export function* initCustomUserSaga(action: PayloadAction<number>) {
  const userId = action.payload;
  yield put(StatsActions.loadStats(userId));
}

export default function* personalizationWatcher() {
  yield all([
    takeLatest(PersonalizationActions.savePersonalizationDomainStat, saveDomainStatSaga),
    takeLatest(PersonalizationActions.savePersonalizationWorkoutStat, saveWorkoutStatSaga),
    takeLatest(PersonalizationActions.savePersonalizationLEDStat, saveLEDStatSaga),
    takeLatest(PersonalizationActions.savePersonalizationTrainingIntensityStat, saveTrainingIngensitySaga),
    takeLatest(PersonalizationActions.saveColourblindnessStat, saveColourblindnessStatSaga),
    takeLatest(PersonalizationActions.saveDyscalculiaStat, saveDyscalculiaStatSaga),
    takeLatest(PersonalizationActions.initUser, initCustomUserSaga),
  ]);
}
